import type React from 'react'

import { createRuntimeIcon } from './createRuntimeIcon'
import { normalizeIconInk } from './svgNormalize'
import type { RuntimeIconProps } from './types'

type IconComponent = React.ComponentType<RuntimeIconProps>
type TenantIconsSource = () => Record<string, string> | undefined

let source: TenantIconsSource = () => undefined

/**
 * Keyed by the raw config markup. Nothing evicts, so the size follows how often the icons map is
 * replaced rather than how many icons a tenant has - which today means once, since config is
 * fetched before the app tree mounts and never refreshed in place. An edited glyph would strand its
 * predecessor's entry; the two renderer caches behave the same way.
 */
const normalizedByMarkup = new Map<string, string>()

/**
 * Registers where tenant artwork comes from, keeping this package free of a config dependency.
 * The source is read lazily on each lookup rather than captured, so it can be registered at module
 * scope — before config has loaded — without an ordering hazard.
 *
 * Because aos-ui-universal is a shared eager singleton, registering once in the host serves every
 * micro-frontend. Holding this state in a non-shared package would repeat the trap the deleted
 * `configureIcons` fell into: that package is not shared, so each remote got its own module
 * instance and its own unset state, and every non-AOS remote silently kept the default.
 */
export const setTenantIconsSource = (next: TenantIconsSource) => {
    source = next
}

/** Direct form for tests and for callers that already hold the map. */
export const setTenantIcons = (icons: Record<string, string> | undefined) => {
    source = () => icons
}

/**
 * Resolves a tenant-supplied glyph by the same name the compiled registry uses, so callers treat it
 * as an override: `iconFor(name)`. Components are cached by markup inside
 * createRuntimeIcon, so nothing here goes stale when config reloads.
 */
export const tenantIcon = (name: string | undefined): IconComponent | undefined => {
    if (!name) {
        return undefined
    }
    // A source that reads config throws until config is initialised. An icon rendering that early
    // should fall back to the compiled glyph, not take the tree down with it.
    let markup: string | undefined
    try {
        const icons = source()
        // Own-property only: a name like `toString` would otherwise resolve an Object.prototype
        // member and render garbage instead of falling through to the compiled glyph.
        markup =
            icons && Object.prototype.hasOwnProperty.call(icons, name) ? icons[name] : undefined
    } catch {
        return undefined
    }
    if (!markup) {
        return undefined
    }
    // Normalise ink here too, not only at the authoring gate: markup can reach config through the
    // JSON view, the seed script or a direct store write, and unnormalised ink silently ignores the
    // consumer's tint. Ink normalisation is idempotent, so re-running it on authored markup is safe
    // - but not free, so it is memoised by raw markup rather than re-scanned on every lookup
    // (createRuntimeIcon caches the component, not the normalisation feeding it).
    let normalized = normalizedByMarkup.get(markup)
    if (normalized === undefined) {
        normalized = normalizeIconInk(markup)
        normalizedByMarkup.set(markup, normalized)
    }
    return createRuntimeIcon(normalized) ?? undefined
}

/** Names the tenant supplies artwork for. Diagnostic surface; no production consumer today. */
export const tenantIconNames = () => {
    try {
        return Object.keys(source() ?? {})
    } catch {
        return []
    }
}
